import { editDistance, plainText, soundKey, tolerance } from "./phonetic";

/** What the driver can say instead of pressing buttons. */
export type VoiceCommand =
    | 'skip'        // next subcard, the current one stays unanswered
    | 'nextCard'    // leave the rest of the card
    | 'repeat'
    | 'slower'
    | 'dontKnow'    // hear the answer, counts as a mistake
    | 'pause'
    | 'resume'
    | 'finish'
    | 'progress'
    | 'help'
    | 'yes'
    | 'no';

/** Phrases per command, Russian and Greek. Matched as a whole utterance, by sound. */
const PHRASES: Record<VoiceCommand, string[]> = {
    skip: [
        'дальше', 'далее', 'пропусти', 'пропустить', 'пропуск', 'следующее', 'следующий',
        'следующее задание', 'следующий вопрос', 'другое', 'επόμενο', 'παρακάτω',
    ],
    nextCard: [
        'следующая карточка', 'следующую карточку', 'другая карточка', 'другую карточку',
        'новая карточка', 'пропусти карточку', 'следующая', 'επόμενη κάρτα',
    ],
    repeat: ['повтори', 'повтори вопрос', 'повторить', 'ещё раз', 'не расслышал', 'не понял', 'ξανά', 'πάλι'],
    slower: ['медленнее', 'помедленнее', 'медленно', 'πιο αργά'],
    dontKnow: [
        'не знаю', 'без понятия', 'сдаюсь', 'скажи ответ', 'ответ', 'какой ответ', 'подскажи',
        'забыл', 'не помню', 'δεν ξέρω',
    ],
    pause: ['пауза', 'стоп', 'подожди', 'погоди', 'παύση', 'σταμάτα'],
    resume: ['продолжай', 'продолжить', 'продолжим', 'поехали', 'συνέχεια', 'συνέχισε'],
    finish: ['закончить', 'заканчиваем', 'закончим', 'хватит', 'конец', 'выход', 'на сегодня всё', 'τέλος'],
    progress: ['сколько осталось', 'сколько ещё', 'прогресс', 'где мы'],
    help: ['команды', 'помощь', 'что говорить', 'что можно сказать', 'βοήθεια'],
    yes: ['да', 'ага', 'давай', 'ещё', 'конечно', 'хочу', 'продолжаем', 'ναι'],
    no: ['нет', 'не надо', 'не хочу', 'όχι'],
};

/** Words that only pad a command: "ну давай дальше пожалуйста" is "дальше". */
const FILLERS = new Set([
    'ну', 'давай', 'пожалуйста', 'так', 'ладно', 'а', 'э', 'эм', 'ммм', 'окей', 'ок', 'слушай', 'теперь',
    'λοιπόν', 'ε',
].map(plainText));

interface CompiledPhrase {
    command: VoiceCommand;
    key: string;
}

const COMPILED: CompiledPhrase[] = (Object.keys(PHRASES) as VoiceCommand[]).flatMap(command =>
    PHRASES[command].map(phrase => ({ command, key: soundKey(phrase) })));

function candidates(transcript: string): string[] {
    const words = plainText(transcript).split(' ').filter(word => word.length > 0);
    const padded = soundKey(words.join(' '));
    const trimmed = words.filter(word => !FILLERS.has(word));
    const bare = trimmed.length > 0 ? soundKey(trimmed.join(' ')) : padded;
    return bare === padded ? [padded] : [bare, padded];
}

/**
 * The commands a transcript could be, best match first. A command must be the
 * whole utterance (fillers aside), so an answer sentence containing "дальше"
 * is not one.
 */
export function matchCommands(transcript: string): VoiceCommand[] {
    const found: { command: VoiceCommand; distance: number }[] = [];
    for (const key of candidates(transcript)) {
        if (key.length === 0)
            continue;
        for (const phrase of COMPILED) {
            const distance = editDistance(key, phrase.key);
            if (distance <= tolerance(phrase.key) && !found.some(entry => entry.command === phrase.command))
                found.push({ command: phrase.command, distance });
        }
    }
    return found.sort((a, b) => a.distance - b.distance).map(entry => entry.command);
}

/** The first command among the transcripts (of one utterance, per language) that is allowed now. */
export function findCommand(transcripts: string[], allowed: ReadonlySet<VoiceCommand>): VoiceCommand | undefined {
    for (const transcript of transcripts) {
        const command = matchCommands(transcript).find(candidate => allowed.has(candidate));
        if (command !== undefined)
            return command;
    }
    return undefined;
}

/** Spoken by "команды" / "помощь". */
export const HELP_TEXT =
    'Отвечайте словом в нужной форме. Команды: дальше — следующее задание. Следующая карточка. ' +
    'Повтори. Медленнее. Не знаю — услышать ответ. Пауза и продолжай. Сколько осталось. Закончить.';
